'use client';

import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useVendor } from '@/contexts/VendorContext';
import { Truck, BadgePercentIcon, MapPin, Tag, X, PhoneCall, Phone } from 'lucide-react';
import { useUser } from '../../contexts/UserContext';
import axiosInstance from '../../utils/axiosInstance';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useState } from 'react';
import { useCart } from '../../hooks/useCart';
import MarketingBanners from './MarketingBanners';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';

const VendorsByCategory = ({ vendors, category }) => {
  const navigate = useNavigate();
  const { user, refreshUser } = useUser();
  const { setSelectedVendor } = useVendor();
  const { clearCart } = useCart();
  const [fullscreenImage, setFullScreenImage] = useState('');

  const sessionToken = localStorage.getItem('sessionToken');
  const refreshToken = localStorage.getItem('refreshToken');

  // Filter vendors that have the selected category or show all if category is "All"
  const filteredVendors =
    category === 'All'
      ? vendors
      : vendors.filter(
          (vendor) => vendor.vendorCategories && vendor.vendorCategories.includes(category)
        );

  const handleVendorSelect = async (vendor) => {
    if (!user) {
      refreshUser();
    }

    const selectedData = {
      vendorName: vendor?.name,
      shopName: vendor?.shopName,
      vendorId: vendor?.id,
      societyName: vendor?.address?.societyName,
      contactNo: vendor?.contactNo,
      alternateContact: vendor.alternateContact,
      status: vendor?.status,
      deliveryCharge: vendor?.deliveryCharge,
      deliveryChargeWaived: vendor?.deliveryChargeWaived,
      minimumOrder: vendor?.minimumOrder,
      vendorCategories: vendor?.vendorCategories,
      qrcodeImage: vendor?.qrcodeImage,
      profileImage: vendor?.profileImage,
      shopImage: vendor?.shopImage,
      offHours: vendor?.offHours,
    };

    try {
      await updateFavoriteVendor(selectedData, user);
      localStorage.setItem('selectedVendor', JSON.stringify(selectedData));
      clearCart();
      navigate('/store');
    } catch (error) {
      console.error(error);
      return;
    }
  };

  const updateFavoriteVendor = async (vendor, user) => {
    const apiData = [
      {
        vendorId: vendor.vendorId || vendor.id,
        societyName: vendor.societyName || vendor.address?.societyName,
        vendorName: vendor.vendorName || vendor.name,
        favourite: true,
        contactNo: vendor.contactNo,
      },
    ];

    await axiosInstance.put(
      `${import.meta.env.VITE_API_URL}/rest/subziwale/api/v1/user/vendors?userId=${user?.id}`,
      apiData,
      {
        headers: {
          sessionToken,
          refreshToken,
        },
      }
    );
  };

  if (filteredVendors.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-gray-500">No vendors available for {category}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3">
      {filteredVendors.map((vendor, idx) => {
        let banneridx = 1;

        if (filteredVendors.length < 2) {
          banneridx = filteredVendors.length - 1;
        }

        if (idx == banneridx) {
          return (
            <>
              <Vendor
                key={vendor.id}
                vendor={vendor}
                setFullScreenImage={setFullScreenImage}
                handleVendorSelect={handleVendorSelect}
              />
              <MarketingBanners />
            </>
          );
        }

        return (
          <Vendor
            key={vendor.id}
            vendor={vendor}
            setFullScreenImage={setFullScreenImage}
            handleVendorSelect={handleVendorSelect}
          />
        );
      })}
      {fullscreenImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
          <button
            className="absolute top-4 right-4 text-white bg-gray-800 p-3 rounded-full"
            onClick={() => setFullScreenImage(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={fullscreenImage || '/placeholder.svg'}
            alt="Fullscreen"
            className="max-w-full max-h-full rounded-lg"
          />
        </div>
      )}
    </div>
  );
};

export default VendorsByCategory;

const Vendor = ({ vendor, handleVendorSelect, setFullScreenImage }) => {
  const shopName = vendor.shopName || `${vendor.name}'s Store`;
  const initial = shopName.charAt(0).toUpperCase();
  const minimumOrder = vendor.minimumOrder;
  const hasOffers = false;
  const hasAlternateContact = !!vendor.alternateContact;
  console.log(hasAlternateContact);

  const handleCall = (phoneNumber) => {
    window.open(`tel:${phoneNumber}`);
  };

  return (
    <motion.div
      key={vendor.id}
      whileTap={{ scale: 0.98 }}
      className="relative bg-white border rounded-lg overflow-hidden shadow-md hover:shadow-lg transition-all duration-200"
    >
      {/* Main content area */}
      <div onClick={() => handleVendorSelect(vendor)} className="cursor-pointer">
        <div className="px-3 py-3">
          <div className="flex items-start gap-4">
            {/* Store Logo */}
            <Avatar className="w-16 h-16 border-2 border-gray-200 shadow-md flex-shrink-0">
              <AvatarImage
                src={vendor.profileImage || `/placeholder.svg?height=64&width=64`}
                alt={shopName}
              />
              <AvatarFallback className="bg-green-100 text-green-800 text-xl">
                {initial}
              </AvatarFallback>
            </Avatar>

            {/* Store Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between w-full">
                <h3 className="font-semibold text-gray-900 text-md leading-tight max-w-[calc(100%-40px)] line-clamp-2">
                  {shopName}
                </h3>

                {/* Phone Call Button with Popover for multiple numbers */}
                <div onClick={(e) => e.stopPropagation()}>
                  {hasAlternateContact ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 rounded-full bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700"
                        >
                          <PhoneCall className="h-5 w-5" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="end">
                        <div className="flex flex-col">
                          <Button
                            variant="ghost"
                            className="flex items-center justify-start gap-2 px-4 py-2 hover:bg-green-50"
                            onClick={() => handleCall(vendor.contactNo)}
                          >
                            <Phone className="h-4 w-4" />
                            <span>Primary: {vendor.contactNo}</span>
                          </Button>
                          <Button
                            variant="ghost"
                            className="flex items-center justify-start gap-2 px-4 py-2 hover:bg-green-50"
                            onClick={() => handleCall(vendor.alternateContact)}
                          >
                            <Phone className="h-4 w-4" />
                            <span>Alternate: {vendor.alternateContact}</span>
                          </Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-full bg-green-50 text-green-600 hover:bg-green-100 hover:text-green-700"
                      onClick={() => handleCall(vendor.contactNo)}
                    >
                      <PhoneCall className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Delivery and Min Order Info */}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {vendor.deliveryChargeWaived && (
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-200 flex items-center gap-1 px-2 py-1"
                  >
                    <Truck className="h-4 w-4" />
                    <span>Free Delivery</span>
                  </Badge>
                )}
                <Badge
                  variant="outline"
                  className="bg-gray-50 text-gray-700 border-gray-200 flex items-center gap-1"
                >
                  <span>Min ₹{minimumOrder}</span>
                </Badge>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="mt-3 flex items-start gap-2">
            <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-500 line-clamp-2">
              {vendor?.address?.unitNo}
              {vendor?.address?.unitNo && ','} {vendor?.address?.societyName},
              {vendor?.address?.plotNo && ` Plot/Pocket No ${vendor?.address?.plotNo}`}
              {vendor?.address?.sector && ` Sector ${vendor?.address?.sector},`}
              {vendor?.address?.locality && ` ${vendor?.address?.locality},`}
              {vendor?.address?.city && ` ${vendor?.address?.city}`}
            </p>
          </div>

          {/* Categories */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Tag className="h-4 w-4 text-gray-400" />
            {vendor?.vendorCategories.map((cat, index) => (
              <Badge
                key={index}
                variant="outline"
                className="bg-green-50 text-green-700 border-green-200 text-xs px-2 py-0.5"
              >
                {cat}
              </Badge>
            ))}
          </div>

          {/* Images */}
          <div className="flex mt-4 items-center gap-3">
            {vendor?.shopImage && (
              <img
                src={vendor?.shopImage || '/placeholder.svg'}
                alt={vendor?.name}
                onClick={(e) => {
                  e.stopPropagation();
                  setFullScreenImage(vendor?.shopImage);
                }}
                className="w-16 h-16 border rounded-md object-cover"
              />
            )}
            {vendor?.qrcodeImage && (
              <img
                src={vendor?.qrcodeImage || '/placeholder.svg'}
                alt={vendor?.name}
                onClick={(e) => {
                  e.stopPropagation();
                  setFullScreenImage(vendor?.qrcodeImage);
                }}
                className="w-16 h-16 border rounded-md object-cover"
              />
            )}
          </div>
        </div>

        {/* Offers Banner */}
        {hasOffers && (
          <div className="mt-2 bg-gradient-to-r from-green-50 to-green-100 px-4 py-2 flex items-center">
            <BadgePercentIcon className="h-5 w-5 text-green-700 mr-2" />
            <span className="text-green-800 font-medium text-sm">Exciting Offers Inside</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};
